import { useLoggedInMember } from "@/modules/auth/states";
import { getZaniaTeamMemberRoles } from "@/modules/team/states";
import { ZButton } from "@/shared/components/button";
import { useFeatureAccess } from "@/shared/hooks/use-feature-access";
import InProgressIcon from "@/shared/icons/inprogress";
import { cn } from "@/shared/utils/classname-merger";
import { Button } from "@nextui-org/react";
import { motion } from "framer-motion";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { bottomSidebarItems, mainSidebarItems } from "../../constants/sidebar";
import { useAgentLogState } from "../../states/agentLog";
import { AgentConsoleNavigation, NavItemData } from "../../types/sidebar";
import { AccordionMenuItem } from "./accordion-menu-item";

const aiAgent = "AI Agent";

interface NavProps {
  isCollapsed: boolean;
  onExpandSidebar?: () => void;
}

export const NavigationMenuItem = ({
  data,
  isCollapsed,
}: {
  data: NavItemData;
  isCollapsed: boolean;
}) => {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const { title, Icon, path, isPlaceholder } = data;
  const isActive =
    pathname === "/" && path === "/" ? true : pathname.includes(path);
  const isAgenLogOpen = useAgentLogState.useIsOpen();
  const setOpenAgentLog = useAgentLogState.useActions().setIsOpen;

  const handleClick = () => {
    if (isPlaceholder) {
      // Placeholder items don't navigate
      return;
    }
    navigate(path);
  };

  if (isCollapsed) {
    return (
      <Button
        onClick={handleClick}
        isIconOnly
        className={cn(
          "h-fit w-fit font-normal text-text-secondary bg-transparent rounded-[12px] px-[16px] py-[10px]",
          isActive && "text-brand-primary font-medium",
          isPlaceholder && "cursor-default opacity-80",
        )}
      >
        <div className="flex flex-col items-center gap-y-[10px]">
          <div className="flex flex-row items-center gap-x-[10px]">
            <Icon
              color={isActive ? "#006FEE" : "#7A788E"}
              strokeWidth={isActive ? 2 : 1.5}
              className="min-h-[20px] min-w-[20px] h-[20px] w-[20px]"
            />
          </div>

          {(pathname.includes(AgentConsoleNavigation.riskAssessment) ||
            pathname.includes(AgentConsoleNavigation.doraAssessment) ||
            pathname.includes(AgentConsoleNavigation.questionnaire)) &&
            title === aiAgent && (
              <div
                className="px-[10px] py-[9px] rounded-[8px] bg-white w-full flex gap-x-[10px]"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenAgentLog(!isAgenLogOpen);
                }}
              >
                <motion.div
                  className="m-0 p-0 flex justify-center items-center"
                  animate={{
                    rotate: [0, 180, 180, 360, 360],
                  }}
                  transition={{
                    repeat: Number.POSITIVE_INFINITY,
                    repeatType: "loop",
                    duration: 4,
                    times: [0, 0.5, 0.75, 1],
                  }}
                >
                  <InProgressIcon
                    className="w-[18px] h-[18px]"
                    color={"#1677FF"}
                  />
                </motion.div>
              </div>
            )}
        </div>
      </Button>
    );
  }

  return (
    <ZButton
      className={cn(
        "justify-start h-nav-item font-normal text-text-secondary bg-transparent rounded-[12px] p-nav-item",
        isActive && "text-brand-primary font-medium",
        isPlaceholder && "cursor-default opacity-80",
      )}
      onClick={handleClick}
    >
      <div className="flex flex-col gap-y-[8px] w-full gap-x-[10px]">
        <div className="flex flex-row items-center gap-x-[10px]">
          <Icon
            color={isActive ? "#006FEE" : "#7A788E"}
            strokeWidth={isActive ? 2 : 1.5}
            className="min-h-[20px] min-w-[20px] h-[20px] w-[20px]"
          />
          <p className="text-[14px] leading-5">{title}</p>
        </div>

        {(pathname.includes(AgentConsoleNavigation.riskAssessment) ||
          pathname.includes(AgentConsoleNavigation.doraAssessment) ||
          pathname.includes(AgentConsoleNavigation.questionnaire)) &&
          title === aiAgent && (
            <div
              className="px-[10px] py-[9px] rounded-[8px] bg-white w-full flex gap-x-[10px]"
              onClick={(e) => {
                e.stopPropagation();
                setOpenAgentLog(!isAgenLogOpen);
              }}
            >
              <motion.div
                className="m-0 p-0 flex justify-center items-center"
                animate={{
                  rotate: [0, 180, 180, 360, 360],
                }}
                transition={{
                  repeat: Number.POSITIVE_INFINITY,
                  repeatType: "loop",
                  duration: 4,
                  times: [0, 0.5, 0.75, 1],
                }}
              >
                <InProgressIcon
                  className="w-[18px] h-[18px]"
                  color={"#1677FF"}
                />
              </motion.div>
              <p className="text-[#1677FF] text-[14px]">In Progress</p>
            </div>
          )}
      </div>
    </ZButton>
  );
};

export default function NavigationMenu({ isCollapsed, onExpandSidebar }: NavProps) {
  const loggedInMember = useLoggedInMember();
  const userPermissions = loggedInMember ? getZaniaTeamMemberRoles("", loggedInMember) : { canAccessZaniaAskAi: false };

  // Combine all sidebar items for feature extraction
  const allSidebarItems = [...mainSidebarItems, ...bottomSidebarItems];

  // Extract all feature names from sidebar items (static extraction)
  const featureNames = allSidebarItems
    .map((item) => item.featureName)
    .filter((name): name is NonNullable<typeof name> => name !== undefined);

  // Get feature access for all features at once
  const featureAccess = useFeatureAccess({ features: featureNames });

  // Filter function for sidebar items
  const filterItems = (items: NavItemData[]) => {
    return items.filter((item) => {
      // Check permission-based access (legacy pattern)
      if (item.canAccess && !item.canAccess(userPermissions)) {
        return false;
      }

      // Check feature flag access (new pattern)
      if (item.featureName) {
        const accessKey = `has${item.featureName.charAt(0).toUpperCase()}${item.featureName.slice(1)}Access` as keyof typeof featureAccess;
        if (!featureAccess[accessKey]) {
          return false;
        }
      }

      return true;
    });
  };

  // Filter main and bottom items separately
  const visibleMainItems = useMemo(
    () => filterItems(mainSidebarItems),
    [userPermissions, featureAccess],
  );

  const visibleBottomItems = useMemo(
    () => filterItems(bottomSidebarItems),
    [userPermissions, featureAccess],
  );

  // Render a nav item (either accordion or regular)
  const renderNavItem = (item: NavItemData) => {
    if (item.children && item.children.length > 0) {
      return (
        <AccordionMenuItem
          key={item.path}
          data={item}
          isCollapsed={isCollapsed}
          onExpandSidebar={onExpandSidebar}
        />
      );
    }
    return (
      <NavigationMenuItem
        key={item.path}
        data={item}
        isCollapsed={isCollapsed}
      />
    );
  };

  return (
    <div className="flex flex-col h-full justify-between">
      {/* Main navigation section (top) */}
      <nav
        className={cn(
          "gap-section-gap px-4",
          isCollapsed
            ? "flex flex-col items-center justify-center gap-3"
            : "flex flex-col",
        )}
      >
        {visibleMainItems.map(renderNavItem)}
      </nav>

      {/* Bottom navigation section */}
      <nav
        className={cn(
          "gap-section-gap px-4",
          isCollapsed
            ? "flex flex-col items-center justify-center gap-3"
            : "flex flex-col",
        )}
      >
        {visibleBottomItems.map(renderNavItem)}
      </nav>
    </div>
  );
}
